import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { RouletteList } from '@/components/roulette/RouletteList'
import { ConfirmDialog } from '@/components/ui'
import { useDeleteRoulette } from '@/hooks/roulette/useRoulettes'
import { captureError } from '@/lib/monitoring'
import type { RouletteWithStats } from '@/types/roulette'

export function RouletteListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [rouletteToDelete, setRouletteToDelete] = useState<RouletteWithStats | null>(null)
  const deleteRouletteMutation = useDeleteRoulette()

  const handleCreateNew = () => {
    navigate('/dashboard/roulette/create')
  }

  const handleViewHistory = (roulette: RouletteWithStats) => {
    navigate(`/dashboard/roulette/${roulette.id}`)
  }

  const handleDeleteRoulette = (roulette: RouletteWithStats) => {
    setRouletteToDelete(roulette)
  }

  const handleConfirmDelete = async () => {
    if (!rouletteToDelete) return

    try {
      await deleteRouletteMutation.mutateAsync(rouletteToDelete.id)
      setRouletteToDelete(null)
    } catch (error) {
      captureError(error as Error, { context: 'Failed to delete roulette' })
    }
  }

  const handleCancelDelete = () => {
    setRouletteToDelete(null)
  }

  const getRouletteProgress = (roulette: RouletteWithStats) => {
    const spunBookIds = new Set(roulette.roulette_spins?.map((spin) => spin.selected_book_id) || [])
    const spunCount = spunBookIds.size
    const availableCount = roulette.book_count - spunCount
    const isCompleted = availableCount === 0 && roulette.book_count > 0

    return {
      available: availableCount,
      total: roulette.book_count,
      spun: spunCount,
      isCompleted,
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <RouletteList
          onCreateNew={handleCreateNew}
          onViewHistory={handleViewHistory}
          onDeleteRoulette={handleDeleteRoulette}
          getRouletteProgress={getRouletteProgress}
        />
      </div>

      <ConfirmDialog
        isOpen={!!rouletteToDelete}
        title={t('roulette.deleteRouletteTitle')}
        message={t('roulette.deleteRouletteMessage', { name: rouletteToDelete?.name })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={deleteRouletteMutation.isPending}
        variant="danger"
      />
    </div>
  )
}
